"use client"

import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle,
  Clock,
  Copy,
  CreditCard,
  ExternalLink,
  RefreshCw,
  Wallet,
  XCircle,
} from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { api } from "../services/api"
import "./TransactionPage.css"

interface TransactionData {
  token: {
    symbol: string
    name: string
    address: string
    logo_url: string
  }
  amount: string
  onchainAmount?: string
  offchainAmount?: string
  chain: string
  depositAddress: string
  network?: string
  networkLogoUrl?: string
  status: string
  bankFolio?: string
  bankFolioURL?: string
  txStatusMessage?: string
  txHash?: string
  clabe?: string
  bank?: string
  beneficiary?: string
  concepto?: string
  createdAt?: string
  expiresAt?: string
}

const TransactionPage: React.FC = () => {
  const { tid } = useParams<{ tid: string }>()
  const [transactionData, setTransactionData] = useState<TransactionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeLeft, setTimeLeft] = useState<{
    hours: number
    minutes: number
    seconds: number
  } | null>(null)

  const navigate = useNavigate()

  // Countdown timer effect - more permissive conditions
  useEffect(() => {
    // Always try to show timer for pending transactions, even without dates
    const showTimer = ["pending_user", "pending_anchor", "pending_ramp", "processing"].includes(
      transactionData?.status || "",
    )

    if (!transactionData || !showTimer) {
      setTimeLeft(null)
      return
    }

    // Create expiry time - use expiresAt if available, otherwise create one
    let expiryTime: Date
    if (transactionData.expiresAt) {
      expiryTime = new Date(transactionData.expiresAt)
    } else if (transactionData.createdAt) {
      expiryTime = new Date(transactionData.createdAt)
      expiryTime.setHours(expiryTime.getHours() + 24) // 24 hours from creation
    } else {
      // Fallback: 24 hours from now if no dates available
      expiryTime = new Date()
      expiryTime.setHours(expiryTime.getHours() + 24)
    }

    const updateTimer = () => {
      const now = new Date().getTime()
      const expiry = expiryTime.getTime()
      const difference = expiry - now

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60))
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((difference % (1000 * 60)) / 1000)
        setTimeLeft({ hours, minutes, seconds })
      } else {
        // Even if expired, show 00:00:00 for a few seconds before hiding
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 })
        setTimeout(() => setTimeLeft(null), 3000)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [transactionData?.createdAt, transactionData?.expiresAt, transactionData?.status])

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null

    const fetchTransactionData = async () => {
      try {
        const token = localStorage.getItem("authToken")
        if (token) {
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`
        } else {
          navigate("/auth")
          return
        }

        const response = await api.get(`/transactions/${tid}`)
        setTransactionData(response.data)
        setLoading(false)

        // WebSocket connection for certain statuses
        if (
          !["pending_user", "pending_anchor", "pending_ramp", "processing", "processing_refund"].includes(
            response.data.status,
          )
        ) {
          if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
          }
          // const ws = new WebSocket(`${import.meta.env.VITE_BACKEND_WEBSOCKET}transactions/${tid}`)
          // ws.onopen = () => {
          //   const message = JSON.stringify({ tid })
          //   ws.send(message)
          // }

          // ws.onmessage = (event) => {
          //   const data = JSON.parse(event.data)
          //   const status = data.txStatus
          //   setTransactionData((prevData) =>
          //     prevData
          //       ? {
          //           ...prevData,
          //           status,
          //           bankFolio: data.bankFolio,
          //           bankFolioURL: data.bankFolioURL,
          //           txStatusMessage: data.txStatusMessage,
          //           txHash: data.txHash,
          //         }
          //       : null,
          //   )
          // }

          // return () => {
          //   ws.close()
          // }
        }
      } catch (error: any) {
        console.error("Error fetching transaction data:", error)
        setError("Error loading transaction data. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    fetchTransactionData()
    // then set up polling every 10 seconds
    intervalId = setInterval(fetchTransactionData, 10_000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [tid, navigate])

  const handleCopyValue = (value: string, label: string) => {
    navigator.clipboard.writeText(value)
    toast.success(`${label} copied to clipboard!`, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    })
  }

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "pending_user":
        return {
          text: "Esperando depósito",
          color: "var(--status-pending)",
          bgColor: "var(--status-pending-bg)",
          icon: Clock,
          pulse: true,
        }
      case "pending_anchor":
      case "processing":
        return {
          text: "Procesando",
          color: "var(--status-processing)",
          bgColor: "var(--status-processing-bg)",
          icon: RefreshCw,
          pulse: true,
        }
      case "processing_refund":
      case "refunding":
        return {
          text: "Procesando Reembolso",
          color: "var(--status-warning)",
          bgColor: "var(--status-warning-bg)",
          icon: RefreshCw,
          pulse: true,
        }
      case "refunded":
        return {
          text: "Reembolso Completado",
          color: "var(--status-refunded)",
          bgColor: "var(--status-refunded-bg)",
          icon: CheckCircle,
          pulse: false,
        }
      case "complete":
        return {
          text: "Confirmado",
          color: "var(--status-success)",
          bgColor: "var(--status-success-bg)",
          icon: CheckCircle,
          pulse: false,
        }
      case "expired":
        return {
          text: "Transacción Expirada",
          color: "var(--status-error)",
          bgColor: "var(--status-error-bg)",
          icon: XCircle,
          pulse: false,
        }
      case "error":
        return {
          text: "Error",
          color: "var(--status-error)",
          bgColor: "var(--status-error-bg)",
          icon: XCircle,
          pulse: false,
        }
      default:
        return {
          text: "Estado desconocido",
          color: "var(--text-color)",
          bgColor: "var(--yuba-light-blue)",
          icon: AlertTriangle,
          pulse: false,
        }
    }
  }

  if (loading) {
    return (
      <div className="transaction-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Cargando transacción...</h3>
          <p>Obteniendo los detalles más recientes</p>
        </div>
      </div>
    )
  }

  if (error || !transactionData) {
    return (
      <div className="transaction-page">
        <div className="error-container">
          <XCircle className="error-icon" />
          <h3>Error al cargar</h3>
          <p>{error || "No se pudieron cargar los datos de la transacción"}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  const statusInfo = getStatusInfo(transactionData.status)
  const StatusIcon = statusInfo.icon
  const isComplete = transactionData.status === "complete"
  const isBuyTransaction = !!transactionData.clabe

  return (
    <div className="transaction-page">
      <div className="page-header">
        <h1>Detalles de Transacción</h1>
        {/* <p>Información completa y estado actual</p> */}
      </div>

      <div className="transaction-container">
        {/* Countdown Timer - Debug version that always shows */}
        {(timeLeft || transactionData?.status === "pending_user") && (
          <div className="countdown-card">
            <div className="countdown-content">
              <Clock className="countdown-icon" />
              <div className="countdown-display">
                <div className="time-unit">
                  <span className="time-number">{timeLeft ? timeLeft.hours.toString().padStart(2, "0") : "24"}</span>
                  <span className="time-label">h</span>
                </div>
                <span className="time-separator">:</span>
                <div className="time-unit">
                  <span className="time-number">{timeLeft ? timeLeft.minutes.toString().padStart(2, "0") : "00"}</span>
                  <span className="time-label">m</span>
                </div>
                <span className="time-separator">:</span>
                <div className="time-unit">
                  <span className="time-number">{timeLeft ? timeLeft.seconds.toString().padStart(2, "0") : "00"}</span>
                  <span className="time-label">s</span>
                </div>
              </div>
              <span className="countdown-text">restante</span>
            </div>
          </div>
        )}

        {/* Status Card */}
        <div className="status-card">
          <div className="status-content">
            <div className="status-icon-container" style={{ backgroundColor: statusInfo.bgColor }}>
              <StatusIcon
                className={`status-icon ${statusInfo.pulse ? "pulse" : ""}`}
                style={{ color: statusInfo.color }}
              />
            </div>
            <div className="status-text">
              <h3 style={{ color: statusInfo.color }}>{statusInfo.text}</h3>
              {transactionData.txStatusMessage && <p className="status-message">{transactionData.txStatusMessage}</p>}
            </div>
          </div>
          {statusInfo.pulse && <div className="status-pulse"></div>}
        </div>

        {/* Transaction Details Card */}
        <div className="transaction-card">
          {isBuyTransaction ? (
            // Buy Transaction Layout
            <div className="buy-transaction">
              <div className="transaction-header">
                <CreditCard className="transaction-icon" />
                <h2>Compra de Criptomoneda</h2>
              </div>

              <div className="amount-display">
                <div className="amount-card primary">
                  <span className="amount-label">Enviar</span>
                  <div className="amount-value">
                    <span className="currency">$</span>
                    <span className="number">{transactionData.amount}</span>
                    <span className="symbol">MXN</span>
                  </div>
                </div>
                <ArrowRight className="arrow-icon" />
                <div className="amount-card secondary">
                  <span className="amount-label">Recibir</span>
                  <div className="amount-value">
                    <img
                      src={transactionData.token.logo_url || "/placeholder.svg"}
                      alt={transactionData.token.symbol}
                      className="token-icon"
                    />
                    <span className="symbol">{transactionData.token.symbol}</span>
                  </div>
                </div>
              </div>

              <div className="bank-details">
                <h3>
                  <Building2 className="section-icon" />
                  Información Bancaria
                </h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Banco</label>
                    <div className="detail-value">
                      <div className="detail-text">{transactionData.bank}</div>
                      <button onClick={() => handleCopyValue(transactionData.bank!, "Banco")} className="copy-btn">
                        <Copy className="copy-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>CLABE</label>
                    <div className="detail-value">
                      <div className="detail-text monospace">{transactionData.clabe}</div>
                      <button onClick={() => handleCopyValue(transactionData.clabe!, "CLABE")} className="copy-btn">
                        <Copy className="copy-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>Beneficiario</label>
                    <div className="detail-value">
                      <div className="detail-text">{transactionData.beneficiary}</div>
                      <button
                        onClick={() => handleCopyValue(transactionData.beneficiary!, "Beneficiario")}
                        className="copy-btn"
                      >
                        <Copy className="copy-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="detail-item full-width">
                    <label>Concepto</label>
                    <div className="detail-value">
                      <div className="detail-text">{transactionData.concepto}</div>
                      <button
                        onClick={() => handleCopyValue(transactionData.concepto!, "Concepto")}
                        className="copy-btn"
                      >
                        <Copy className="copy-icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Sell Transaction Layout
            <div className="sell-transaction">
              <div className="transaction-header">
                <Wallet className="transaction-icon" />
                <h2>Venta de Criptomoneda</h2>
              </div>

              <div className="amount-display">
                <div className="amount-card primary">
                  <span className="amount-label">Enviar</span>
                  <div className="amount-value">
                    <img
                      src={transactionData.token.logo_url || "/placeholder.svg"}
                      alt={transactionData.token.symbol}
                      className="token-icon"
                    />
                    <span className="number">{transactionData.onchainAmount}</span>
                    <span className="symbol">{transactionData.token.symbol}</span>
                  </div>
                </div>
                <ArrowRight className="arrow-icon" />
                <div className="amount-card secondary">
                  <span className="amount-label">Recibir</span>
                  <div className="amount-value">
                    <span className="currency">$</span>
                    <span className="number">{transactionData.offchainAmount}</span>
                    <span className="symbol">MXN</span>
                  </div>
                </div>
              </div>

              <div className="network-info">
                <div className="network-badge">
                  <img
                    src={transactionData.networkLogoUrl || "/placeholder.svg"}
                    alt={transactionData.network}
                    className="network-icon"
                  />
                  <span>{transactionData.network}</span>
                </div>
              </div>

              <div className="wallet-details">
                <h3>
                  <Wallet className="section-icon" />
                  Dirección de Envío
                </h3>
                <div className="address-container">
                  <p className="instruction">
                    Envía tu {transactionData.token.symbol} a esta dirección en {transactionData.network}:
                  </p>
                  <div className="address-card">
                    <div className="address-text">{transactionData.depositAddress}</div>
                    <button
                      onClick={() => handleCopyValue(transactionData.depositAddress, "Dirección")}
                      className="copy-btn"
                    >
                      <Copy className="copy-icon" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Complete Section */}
          {isComplete && (
            <div className="completion-details">
              <div className="completion-header">
                <CheckCircle className="completion-icon" />
                <h3>Transacción Completada</h3>
              </div>

              {transactionData.bankFolio && transactionData.bankFolioURL && (
                <div className="completion-item">
                  <label>Folio Bancario</label>
                  <a
                    href={`${transactionData.bankFolioURL}${transactionData.bankFolio}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    Ver comprobante bancario
                    <ExternalLink className="external-icon" />
                  </a>
                </div>
              )}

              {transactionData.txHash && (
                <div className="completion-item">
                  <label>Hash de Transacción</label>
                  <div className="hash-container">
                    <div className="hash-text">{transactionData.txHash}</div>
                    <div className="hash-actions">
                      <button
                        onClick={() => handleCopyValue(transactionData.txHash!, "Hash de transacción")}
                        className="copy-btn"
                      >
                        <Copy className="copy-icon" />
                      </button>
                      <a
                        href={`https://etherscan.io/tx/${transactionData.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-btn"
                      >
                        <ExternalLink className="external-icon" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  )
}

export default TransactionPage
